import { driver, DriveStep } from 'driver.js'
import i18n from 'i18next'
import { Classes } from 'jss'

import endUserManual from '../../../assets/ai-go-studio-end-user-manual.pdf'
import { USER_LEVEL } from '../../../components/Settings/definitions'
import { Settings } from '../../../components/Settings/types'
import { ProductType } from '../../../types'
import getAnomalyCustomDriverSteps from './CustomDriverSteps/anomaly'
import getClassificationCustomDriverSteps from './CustomDriverSteps/classification'
import getPatchCustomDriverSteps from './CustomDriverSteps/patch'
import getSegmentationCustomDriverSteps from './CustomDriverSteps/segmentation'

const TITLE_DRIVER = i18n.t('New training')

const getDriver = (
  classesDriver: Classes<'driver'>,
  settings: Settings,
  updateSettings: (settings: Partial<Settings>) => void,
  updatePreference: () => void,
  productType: ProductType
) => {
  const welcomeDriveStep: DriveStep = {
    popover: {
      title: i18n.t('Welcome to AI-go Studio!'),
      description: `
              <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">          
              ${i18n.t(
                'This guided tour will help you understand the main functionalities of AI-go Studio.'
              )}
              <br/><br/>
              ${i18n.t(
                'You can always access the guided tour by clicking on the'
              )}
              <b><i style="font-size:15px" class="fa">&#xf059;</i></b>
              ${i18n.t('icon in the upper right corner.')}
            `,
      side: 'bottom',
      align: 'start',
    },
  }

  const datasetDriveStep: DriveStep = {
    element: '#divDatasetId',
    popover: {
      title: TITLE_DRIVER,
      description: i18n.t(
        'Optionally select an images dataset to be used in training.'
      ),
      side: 'bottom',
      align: 'start',
    },
  }

  const headerImagesDriveStep: DriveStep = {
    element: '#divHeaderImagesId',
    popover: {
      title: TITLE_DRIVER,
      description: `${i18n.t(
        'Upload images to populate the dataset. You can upload images in bulk or one by one.'
      )}<br/>
      ${
        productType === 'segmentation' || productType === 'classification_patch'
          ? `${i18n.t(
              'You can also import custom annotations via zip file with a predefined structure (see details in end user manual).'
            )}<br/><br/>`
          : ''
      }
      ${i18n.t('Images can be sorted, filtered, downloaded, and deleted.')}`,
      side: 'bottom',
      align: 'start',
    },
  }

  const imagesDriveStep: DriveStep = {
    element: '#divImagesId',
    popover: {
      title: TITLE_DRIVER,
      description: i18n.t('Select the images to use in training.'),
      side: 'bottom',
      align: 'start',
    },
  }

  const endDriveSteps: DriveStep[] = [
    {
      element: '#divTrainingNameId',
      popover: {
        title: TITLE_DRIVER,
        description: i18n.t('Enter the name of the new training.'),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#divTrainingNotesId',
      popover: {
        title: TITLE_DRIVER,
        description: i18n.t('Enter the notes of the new training.'),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#startTestSwitchId',
      popover: {
        title: TITLE_DRIVER,
        description: `${i18n.t(
          'It is possible to start a test with unselected images. This will allow you to test the model with images that have not been used for training.'
        )}<br/>
            ${i18n.t(
              'The test will start automatically when the training is completed.'
            )}`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#divTrainingNotificationsId',
      popover: {
        title: TITLE_DRIVER,
        description: i18n.t(
          'It is possible to enable email notification. An email will be sent upon completion of the training.'
        ),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#startTrainingButtonId',
      popover: {
        title: TITLE_DRIVER,
        description: `${i18n.t(
          'After selecting all the necessary fields, start the training by clicking'
        )} 
        <b>${i18n.t('Start a new training')}</b>. 
        ${i18n.t('Happy exploring!')}<br/><br/>
        ${i18n.t('Additional information can be found in the')}
        <a target='_blank' href='${endUserManual}'>${i18n.t(
          'End user manual (PDF)'
        )}</a>
            <br/><br/>
            <label htmlFor="doNotShowAgainCheckbox">
            <input
              type="checkbox"
              id="doNotShowAgainCheckbox"
              ${settings.preference__show_guided_tour ? '' : 'checked'}
            />
            ${i18n.t('Do not show again')}
            </label>`,
        side: 'bottom',
        align: 'start',
        onPopoverRender: () => {
          document
            .getElementById('doNotShowAgainCheckbox')
            ?.addEventListener('change', () => {
              updateSettings({
                preference__show_guided_tour: !settings.preference__show_guided_tour,
              })
            })
        },
        onNextClick: () => {
          updatePreference()
          driverObj.destroy()
        },
      },
    },
  ]

  const initialDriveSteps: DriveStep[] = []
  initialDriveSteps.push(welcomeDriveStep)
  if (settings.preference__user_level === USER_LEVEL.expert) {
    initialDriveSteps.push(datasetDriveStep)
  }
  initialDriveSteps.push(headerImagesDriveStep)
  initialDriveSteps.push(imagesDriveStep)

  const customDriveSteps: DriveStep[] = {
    anomaly: getAnomalyCustomDriverSteps(settings.preference__user_level),
    classification: getClassificationCustomDriverSteps(),
    classification_patch: getPatchCustomDriverSteps(),
    segmentation: getSegmentationCustomDriverSteps(
      settings.preference__user_level
    ),
  }[productType]

  const driverObj = driver({
    showProgress: true,
    steps: customDriveSteps
      ? initialDriveSteps.concat(customDriveSteps).concat(endDriveSteps)
      : initialDriveSteps.concat(endDriveSteps),
    nextBtnText: i18n.t('Next'),
    prevBtnText: i18n.t('Previous'),
    doneBtnText: i18n.t('Done'),
    popoverClass: classesDriver.driver,
    onPopoverRender: (popover) => {
      const skipButton = document.createElement('button')
      skipButton.id = 'skipButton'
      skipButton.innerText = i18n.t('Skip all')
      popover.footerButtons.appendChild(skipButton)
      skipButton.addEventListener('click', () => {
        const stepsLength = driverObj.getConfig('steps')?.length
        if (stepsLength) {
          driverObj.drive(stepsLength - 1)
        }
      })
    },
    onDestroyStarted: () => {
      if (driverObj.isLastStep()) {
        updatePreference()
      }
      driverObj.destroy()
    },
  })

  return driverObj
}

export default getDriver
